import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './masive-measurements-qc.scss';
import DataGrid, { Column, Editing, KeyboardNavigation, Scrolling, Sorting, StateStoring, Selection, FilterRow, HeaderFilter } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import { useApiMeasurementsQC } from '../../api/api.measurementsQC';
import { useApiConfigQC } from '../../api/api.configQC';
import { ConfiguracionQcDTO, CreateMedicionQcDTO, MedicionQcDTO, TipoResultadoQC, ClasificacionSigma } from '../../types/types.qualityControl';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useAppSelector } from '../../contexts/hooks';
import { newToast } from '../../components';
import TagBox from 'devextreme-react/tag-box';
import { Chart, Series, ArgumentAxis, ValueAxis, Legend, Tooltip as ChartTooltip, CommonSeriesSettings, Label, Point, Animation } from 'devextreme-react/chart';
import ScrollView from 'devextreme-react/scroll-view';
import { Toolbar } from 'devextreme-react/toolbar';
import { Item as ToolbarItem } from 'devextreme-react/toolbar';
import { resButtonRefresh } from '../../images/icons';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import Popup from 'devextreme-react/popup';
import TextArea from 'devextreme-react/text-area';
import CheckBox from 'devextreme-react/check-box';
import { resGridNotes, resGridNotesCheck } from '../../images/icons';
import { ConvertLocalDate } from '../../utils/functions';
import LoadPanel from 'devextreme-react/load-panel';
import QcReports from './qc-reports';

// ─── Helpers de clasificación Sigma ───
const SIGMA_COLORS: Record<ClasificacionSigma, string> = {
  CLASE_MUNDIAL: '#4CAF50',
  EXCELENTE: '#2196F3',
  BUENO: '#FFC107',
  MARGINAL: '#FF9800',
  INACEPTABLE: '#F44336',
};

const SIGMA_LABELS: Record<ClasificacionSigma, string> = {
  CLASE_MUNDIAL: 'Clase Mundial',
  EXCELENTE: 'Excelente',
  BUENO: 'Bueno',
  MARGINAL: 'Marginal',
  INACEPTABLE: 'Inaceptable',
};

const getSigmaColor = (clasif?: ClasificacionSigma | string | null): string => {
  if (!clasif) return '#9E9E9E';
  return SIGMA_COLORS[clasif as ClasificacionSigma] || '#9E9E9E';
};

const getSigmaLabel = (clasif?: ClasificacionSigma | string | null): string => {
  if (!clasif) return 'N/D';
  return SIGMA_LABELS[clasif as ClasificacionSigma] || String(clasif);
};

// ─── Filtro de configuraciones activas ───
// Solo muestra configs con Qcc_Estado !== false (excluye inactivas).
const filterActiveConfigs = (configs: ConfiguracionQcDTO[] | null | undefined): ConfiguracionQcDTO[] => {
  if (!configs) return [];
  // Excluir explícitamente inactivas: boolean false o entero 0 (según serialización del backend)
  return configs.filter((c) => c.Qcc_Estado !== false && (c.Qcc_Estado as any) !== 0);
};

// ─── Caché persistente en sessionStorage para configs QC ───
const QC_CACHE_PREFIX = 'qc_configs_sede_';
const QC_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutos

interface QcCacheEntry {
  timestamp: number;
  sedeId: number;
  configs: ConfiguracionQcDTO[];
}

function getQcCacheFromSession(sedeId: number): ConfiguracionQcDTO[] | null {
  try {
    const raw = sessionStorage.getItem(QC_CACHE_PREFIX + sedeId);
    if (!raw) return null;
    const entry: QcCacheEntry = JSON.parse(raw);
    if (entry.sedeId !== sedeId) return null;
    if (Date.now() - entry.timestamp > QC_CACHE_TTL_MS) return null;
    return entry.configs;
  } catch {
    return null;
  }
}

function setQcCacheToSession(sedeId: number, configs: ConfiguracionQcDTO[]): void {
  try {
    const entry: QcCacheEntry = { timestamp: Date.now(), sedeId, configs };
    sessionStorage.setItem(QC_CACHE_PREFIX + sedeId, JSON.stringify(entry));
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}

const MasiveMeasurementsQC = () => {
  // Permisos
  const Prm_QC_R = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_R) ?? false;
  const Prm_QC_C = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_C) ?? false;
  const Prm_QC_InformesSigma = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_InformesSigma) ?? false;  
  const userId = useAppSelector((x) => x.userInfo.User?.Us_Id) ?? 0;
  const userLocation = Number(useAppSelector((x) => x.userInfo.Location) ?? 0);

  // API Hooks
  const { registerMeasurement, updateMeasurement, getLeveyJenningsData, getMeasurementsByConfiguration } = useApiMeasurementsQC();
  const { getConfigurationsBySede } = useApiConfigQC();

  // Referencias
  const gridRef = useRef<DataGrid>(null);

  // Estados
  const [isLoadingControles, setIsLoadingControles] = useState(false);
  const [materialesLotes, setMaterialesLotes] = useState<Array<{ MaterialLote: string; MaterialNombre: string; LoteCodigo: string; MaterialNivel: string }>>([]);
  const [selectedMaterialLotes, setSelectedMaterialLotes] = useState<string[]>([]);
  const [analitos, setAnalitos] = useState<any[]>([]);
  const [analitoDataSource, setAnalitoDataSource] = useState<DataSource | null>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [selectedAnalitoId, setSelectedAnalitoId] = useState<number | null>(null);
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const [selectedAnalitoNombre, setSelectedAnalitoNombre] = useState<string>('');
  const [chartsData, setChartsData] = useState<Array<{ controlKey: string; controlName: string; data: any }>>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [medicionesAnalito, setMedicionesAnalito] = useState<MedicionQcDTO[]>([]);
  const medicionesGridRef = useRef<DataGrid>(null);
  const [configuracionesMap, setConfiguracionesMap] = useState<Map<string, ConfiguracionQcDTO>>(new Map());

  // Cachés para optimización de rendimiento
  const [configuracionesCache, setConfiguracionesCache] = useState<ConfiguracionQcDTO[] | null>(null);
  const [medicionesCache, setMedicionesCache] = useState<Map<number, MedicionQcDTO[]>>(new Map());
  const [graficosCache, setGraficosCache] = useState<Map<number, any>>(new Map());

  // Estados para el modal de notas
  const [notaModalVisible, setNotaModalVisible] = useState(false);
  const [notaActual, setNotaActual] = useState('');
  const [notaAnalitoId, setNotaAnalitoId] = useState<number | null>(null);
  const [notaMaterialLoteKey, setNotaMaterialLoteKey] = useState<string>('');

  // Estados de carga
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Estados para controles de visualización de gráficos
  const [nivelesJuntos, setNivelesJuntos] = useState(false);
  const [mostrarLineas, setMostrarLineas] = useState(true);

  useEffect(() => {
    if (Prm_QC_R) {
      loadMaterialesLotes();
    }
  }, [Prm_QC_R, userLocation]);

  const loadMaterialesLotes = async () => {
    try {
      // 1. Intentar cargar desde sessionStorage para mostrar el combobox al instante
      const cachedConfigs = filterActiveConfigs(getQcCacheFromSession(userLocation));
      if (cachedConfigs && cachedConfigs.length > 0) {
        setConfiguracionesCache(cachedConfigs);
        applyConfigsToCombobox(cachedConfigs);
        // Refrescar en background para que configuraciones activadas/desactivadas
        // se reflejen sin bloquear la UI
        refreshConfigsInBackground();
        return;
      }

      // 2. Sin caché — cargar desde API (mostrar indicador de carga)
      setIsLoadingControles(true);
      const configs = filterActiveConfigs(await getConfigurationsBySede(userLocation, true));
      if (configs && configs.length > 0) {
        setConfiguracionesCache(configs);
        setQcCacheToSession(userLocation, configs);
        applyConfigsToCombobox(configs);
      } else {
        setMaterialesLotes([]);
      }
    } catch (error) {
      console.error('Error cargando materiales/lotes:', error);
      newToast('ERROR', 'Error al cargar controles de calidad', 5);
    } finally {
      setIsLoadingControles(false);
    }
  };

  const applyConfigsToCombobox = (configs: ConfiguracionQcDTO[]) => {
    const grouped = configs.reduce((acc: any, config) => {
      const key = `${config.MaterialNombre}|${config.LoteCodigo}|${config.MaterialNivel}`;
      if (!acc[key]) {
        acc[key] = {
          MaterialLote: key,
          MaterialNombre: config.MaterialNombre,
          LoteCodigo: config.LoteCodigo,
          MaterialNivel: config.MaterialNivel,
          DisplayText: `${config.MaterialNombre} - ${config.MaterialNivel} - Lote: ${config.LoteCodigo}`,
        };
      }
      return acc;
    }, {});
    setMaterialesLotes(Object.values(grouped));
  };

  const refreshConfigsInBackground = async () => {
    try {
      const freshConfigs = filterActiveConfigs(await getConfigurationsBySede(userLocation, true));
      if (freshConfigs && freshConfigs.length > 0) {
        setConfiguracionesCache(freshConfigs);
        setQcCacheToSession(userLocation, freshConfigs);
        applyConfigsToCombobox(freshConfigs);
      }
    } catch {
      // Background refresh failed — stale cache is still usable
    }
  };

  // Ref para cancelar cargas en background cuando cambia la selección
  const loadingAbortRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  const handleMaterialLoteChange = async (materialLotes: string[]) => {
    // Cancelar cualquier carga en background anterior
    loadingAbortRef.current.cancelled = true;
    const currentLoad = { cancelled: false };
    loadingAbortRef.current = currentLoad;

    setSelectedMaterialLotes(materialLotes);
    setSelectedAnalitoId(null);
    setSelectedConfigId(null);
    setSelectedAnalitoNombre('');
    setChartData(null);
    setMedicionesAnalito([]);
    setSelectedTab(0);

    if (!materialLotes || materialLotes.length === 0) {
      setAnalitos([]);
      setAnalitoDataSource(null);
      setIsLoading(false);
      return;
    }

    try {
      // Usar caché de configuraciones: useState > sessionStorage > API
      let configs = configuracionesCache;
      if (!configs) {
        configs = filterActiveConfigs(getQcCacheFromSession(userLocation));
        if (configs && configs.length > 0) {
          setConfiguracionesCache(configs);
        } else {
          configs = null;
        }
      }
      if (!configs) {
        setIsLoading(true);
        configs = filterActiveConfigs(await getConfigurationsBySede(userLocation, true));
        if (currentLoad.cancelled) return;
        setConfiguracionesCache(configs);
        setQcCacheToSession(userLocation, configs);
      }

      // Crear mapa de configuraciones para acceso rápido
      const configMap = new Map<string, ConfiguracionQcDTO>();

      // Filtrar configuraciones seleccionadas
      const selectedConfigs = configs.filter((c) => {
        const key = `${c.MaterialNombre}|${c.LoteCodigo}|${c.MaterialNivel}`;
        return materialLotes.includes(key);
      });

      // Mapear configuraciones por Material-Lote-Nivel
      selectedConfigs.forEach((config) => {
        const key = `${config.MaterialNombre}|${config.LoteCodigo}|${config.MaterialNivel}`;
        configMap.set(`${key}|${config.Qcc_An_Id}`, config);
      });

      setConfiguracionesMap(configMap);

      // ═══════════════════════════════════════════════════════════════
      // PASO 1: Construir grid INMEDIATAMENTE con datos de config
      //         (sin esperar ninguna llamada HTTP adicional)
      //         Si hay datos en caché, los usa; si no, muestra pending
      // ═══════════════════════════════════════════════════════════════
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const analitosByAnId = new Map<number, any>();
      const configsToLoad: ConfiguracionQcDTO[] = [];

      // Mapa de valores actuales del grid para preservarlos si PASO 2 se cancela
      // Clave: `${Qcc_An_Id}|${materialLoteKey}`
      const existingControlValues = new Map<string, any>();
      analitos.forEach((a) => {
        Object.entries(a.controles || {}).forEach(([mlKey, ctrl]: [string, any]) => {
          existingControlValues.set(`${a.Qcc_An_Id}|${mlKey}`, ctrl);
        });
      });

      selectedConfigs.forEach((config) => {
        const materialLoteKey = `${config.MaterialNombre}|${config.LoteCodigo}|${config.MaterialNivel}`;

        if (!analitosByAnId.has(config.Qcc_An_Id)) {
          analitosByAnId.set(config.Qcc_An_Id, {
            Qcc_An_Id: config.Qcc_An_Id,
            AnalitoNombre: config.AnalitoNombre || '',
            controles: {},
          });
        }

        const analito = analitosByAnId.get(config.Qcc_An_Id);

        // Revisar si ya hay dato en caché
        const cachedMediciones = medicionesCache.get(config.Qcc_Id);
        let medicionExistente: any = null;
        if (cachedMediciones) {
          medicionExistente = cachedMediciones.find((m: any) => {
            const fechaMedicion = new Date(ConvertLocalDate(m.Qcme_FechaHora));
            return fechaMedicion >= today && fechaMedicion < tomorrow;
          });
        }
        // Si no hay caché o no se encontró la medición de hoy, marcar para recargar desde API
        if (!medicionExistente) {
          configsToLoad.push(config);
        }

        // Valores existentes en el grid (para preservarlos si PASO 2 se cancela)
        const existingCtrl = existingControlValues.get(`${config.Qcc_An_Id}|${materialLoteKey}`);

        analito.controles[materialLoteKey] = {
          Qcc_Id: config.Qcc_Id,
          MaterialNombre: config.MaterialNombre,
          MaterialNivel: config.MaterialNivel,
          LoteCodigo: config.LoteCodigo,
          Qcc_MediaEsperada: config.Qcc_MediaEsperada,
          Qcc_DesviacionEst: config.Qcc_DesviacionEst,
          Qcc_N: config.Qcc_N,
          TipoResultado: (config.Qcc_TipoResultado || 'CUANTITATIVO') as TipoResultadoQC,
          ValoresEsperados: config.ValoresEsperados || [],
          // Prioridad: medición de caché → valores existentes en grid → null
          Valor: medicionExistente?.Qcme_Valor ?? existingCtrl?.Valor ?? null,
          ValorCualitativo: medicionExistente?.Qcme_ValorCualitativo ?? existingCtrl?.ValorCualitativo ?? null,
          Aprobado: medicionExistente?.Qcme_Aprobado ?? existingCtrl?.Aprobado ?? null,
          AprobadoTexto: medicionExistente?.AprobadoTexto ?? existingCtrl?.AprobadoTexto ?? null,
          ResultadoTexto: medicionExistente?.ResultadoTexto ?? existingCtrl?.ResultadoTexto ?? null,
          Estado: medicionExistente ? 'saved' : (existingCtrl?.Estado ?? 'pending'),
          Qcme_Id: medicionExistente?.Qcme_Id ?? existingCtrl?.Qcme_Id,
          Qcme_FechaHora: medicionExistente?.Qcme_FechaHora ?? existingCtrl?.Qcme_FechaHora,
          ZScore: medicionExistente?.ZScore ?? existingCtrl?.ZScore,
          Qcme_Estado: medicionExistente?.Qcme_Estado ?? existingCtrl?.Qcme_Estado,
          TieneViolaciones: medicionExistente?.TieneViolaciones ?? existingCtrl?.TieneViolaciones,
          Observacion: medicionExistente?.Qcme_Observaciones ?? existingCtrl?.Observacion,
          Nota: medicionExistente?.Qcme_ComentarioValidacion ?? existingCtrl?.Nota ?? '',
        };
      });

      const analitosList = Array.from(analitosByAnId.values());
      setAnalitos(analitosList);

      // Crear CustomStore para manejar las actualizaciones
      const customStore = new CustomStore({
        key: 'Qcc_An_Id',
        load: () => {
          return new Promise((resolve) => {
            setAnalitos((current) => {
              resolve(current);
              return current;
            });
          });
        },
        update: async (key: number, values: any) => {
          // Leer el estado actual del analito desde el state (no del closure)
          let currentAnalito: any = null;
          await new Promise<void>((resolve) => {
            setAnalitos((current) => {
              currentAnalito = current.find((a: any) => a.Qcc_An_Id === key);
              resolve();
              return current;
            });
          });

          if (!currentAnalito) {
            console.error('No se encontró el analito con Qcc_An_Id:', key);
            return Promise.reject('Analito no encontrado');
          }

          const updatePromises: Promise<any>[] = [];
          const savingFlags = new Map<string, boolean>();

          for (const [controlKey, newValue] of Object.entries(values)) {
            let materialLoteKey: string;
            let isQualitativeColumn = false;

            if (controlKey.startsWith('ValorCual_')) {
              materialLoteKey = controlKey.replace('ValorCual_', '');
              isQualitativeColumn = true;
            } else if (controlKey.startsWith('Valor_')) {
              materialLoteKey = controlKey.replace('Valor_', '');
            } else {
              continue;
            }

            if (newValue === null || newValue === undefined) continue;

            const control = currentAnalito.controles[materialLoteKey];

            if (!control) {
              console.error('No se encontró control:', materialLoteKey);
              continue;
            }

            const saveKey = `${key}_${materialLoteKey}`;
            if (savingFlags.get(saveKey)) {
              continue;
            }
            savingFlags.set(saveKey, true);

            const tipoResultado: TipoResultadoQC = control.TipoResultado || 'CUANTITATIVO';
            setAnalitos((prev) =>
              prev.map((a) => {
                if (a.Qcc_An_Id === key) {
                  const updatedControl = {
                    ...a.controles[materialLoteKey],
                    Estado: 'saving',
                  };
                  if (isQualitativeColumn || tipoResultado === 'CUALITATIVO') {
                    updatedControl.ValorCualitativo = newValue;
                  } else {
                    updatedControl.Valor = newValue;
                  }
                  return {
                    ...a,
                    controles: {
                      ...a.controles,
                      [materialLoteKey]: updatedControl,
                    },
                  };
                }
                return a;
              })
            );

            const savePromise = (async () => {
              try {
                let result;
                let esActualizacion = false;
                let medicionId = control.Qcme_Id;

                if (medicionId) {
                  esActualizacion = true;
                  const updateDto: any = {
                    Qcme_Id: medicionId,
                    Qcme_Qcc_Id: control.Qcc_Id,
                    Qcme_Us_Id: userId,
                    Qcme_ComentarioValidacion: control.Nota || undefined,
                  };

                  if (isQualitativeColumn || tipoResultado === 'CUALITATIVO') {
                    updateDto.Qcme_ValorCualitativo = newValue as string;
                    if (tipoResultado === 'SEMICUANTITATIVO' && control.Valor != null) {
                      updateDto.Qcme_Valor = control.Valor;
                    }
                  } else {
                    updateDto.Qcme_Valor = newValue as number;
                    if (tipoResultado === 'SEMICUANTITATIVO' && control.ValorCualitativo) {
                      updateDto.Qcme_ValorCualitativo = control.ValorCualitativo;
                    }
                  }

                  result = await updateMeasurement(updateDto);
                } else {
                  const medicionesActuales = await getMeasurementsByConfiguration(control.Qcc_Id);
                  
                  const todayCheck = new Date();
                  todayCheck.setHours(0, 0, 0, 0);
                  const tomorrowCheck = new Date(todayCheck);
                  tomorrowCheck.setDate(tomorrowCheck.getDate() + 1);

                  const medicionHoy = medicionesActuales?.find((m: any) => {
                    const fechaMedicion = new Date(ConvertLocalDate(m.Qcme_FechaHora));
                    return fechaMedicion >= todayCheck && fechaMedicion < tomorrowCheck;
                  });

                  if (medicionHoy) {
                    esActualizacion = true;
                    medicionId = medicionHoy.Qcme_Id;
                    const updateDto: any = {
                      Qcme_Id: medicionHoy.Qcme_Id,
                      Qcme_Qcc_Id: control.Qcc_Id,
                      Qcme_Us_Id: userId,
                      Qcme_ComentarioValidacion: control.Nota || undefined,
                    };

                    if (isQualitativeColumn || tipoResultado === 'CUALITATIVO') {
                      updateDto.Qcme_ValorCualitativo = newValue as string;
                      if (tipoResultado === 'SEMICUANTITATIVO' && control.Valor != null) {
                        updateDto.Qcme_Valor = control.Valor;
                      }
                    } else {
                      updateDto.Qcme_Valor = newValue as number;
                      if (tipoResultado === 'SEMICUANTITATIVO' && control.ValorCualitativo) {
                        updateDto.Qcme_ValorCualitativo = control.ValorCualitativo;
                      }
                    }

                    result = await updateMeasurement(updateDto);
                  } else {
                    const createDto: CreateMedicionQcDTO = {
                      Qcme_Qcc_Id: control.Qcc_Id,
                      Qcme_ComentarioValidacion: control.Nota || undefined,
                      Qcme_Us_Id: userId,
                    };

                    if (isQualitativeColumn || tipoResultado === 'CUALITATIVO') {
                      createDto.Qcme_ValorCualitativo = newValue as string;
                      if (tipoResultado === 'SEMICUANTITATIVO' && control.Valor != null) {
                        createDto.Qcme_Valor = control.Valor;
                      }
                    } else {
                      createDto.Qcme_Valor = newValue as number;
                      if (tipoResultado === 'SEMICUANTITATIVO' && control.ValorCualitativo) {
                        createDto.Qcme_ValorCualitativo = control.ValorCualitativo;
                      }
                    }

                    result = await registerMeasurement(createDto);
                    medicionId = result?.Qcme_Id;
                  }
                }

                const fechaHora = result?.Qcme_FechaHora || control.Qcme_FechaHora;

                setAnalitos((prev) =>
                  prev.map((a) => {
                    if (a.Qcc_An_Id === key) {
                      return {
                        ...a,
                        controles: {
                          ...a.controles,
                          [materialLoteKey]: {
                            ...a.controles[materialLoteKey],
                            Valor: result?.Qcme_Valor ?? a.controles[materialLoteKey]?.Valor,
                            ValorCualitativo: result?.Qcme_ValorCualitativo ?? a.controles[materialLoteKey]?.ValorCualitativo,
                            Aprobado: result?.Qcme_Aprobado ?? a.controles[materialLoteKey]?.Aprobado,
                            AprobadoTexto: result?.AprobadoTexto ?? a.controles[materialLoteKey]?.AprobadoTexto,
                            ResultadoTexto: result?.ResultadoTexto ?? a.controles[materialLoteKey]?.ResultadoTexto,
                            Estado: 'saved',
                            Qcme_Id: medicionId,
                            Qcme_FechaHora: fechaHora,
                            ZScore: result?.ZScore ?? a.controles[materialLoteKey]?.ZScore,
                            Qcme_Estado: result?.Qcme_Estado || 2,
                            TieneViolaciones: result?.TieneViolaciones || false,
                          },
                        },
                      };
                    }
                    return a;
                  })
                );

                setMedicionesCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.delete(control.Qcc_Id);
                  return newCache;
                });

                const accion = esActualizacion ? 'actualizada' : 'registrada';
                newToast('SUCCESS', `Medición ${accion} para ${currentAnalito.AnalitoNombre} - ${control.MaterialNombre}`, 2);
              } catch (error) {
                console.error('Error guardando medición:', error);
                savingFlags.delete(`${key}_${materialLoteKey}`);
                setAnalitos((prev) =>
                  prev.map((a) => {
                    if (a.Qcc_An_Id === key) {
                      return {
                        ...a,
                        controles: {
                          ...a.controles,
                          [materialLoteKey]: {
                            ...a.controles[materialLoteKey],
                            Estado: 'error',
                            Valor: newValue,
                          },
                        },
                      };
                    }
                    return a;
                  })
                );
                newToast('ERROR', `Error al guardar medición para ${currentAnalito.AnalitoNombre}`, 5);
                throw error;
              }
            })();

            updatePromises.push(savePromise);
          }

          if (updatePromises.length > 0) {
            await Promise.all(updatePromises);
          }

          return Promise.resolve();
        },
      });

      const dataSource = new DataSource({
        store: customStore,
        reshapeOnPush: true,
      });

      setAnalitoDataSource(dataSource);
      setIsLoading(false);

      // ═══════════════════════════════════════════════════════════════
      // PASO 2: Cargar mediciones del día en BACKGROUND por lotes
      //         El grid ya es visible y editable — esto solo llena
      //         los valores de mediciones existentes del día actual
      // ═══════════════════════════════════════════════════════════════
      if (configsToLoad.length > 0) {
        const BATCH_SIZE = 15;
        const newCacheEntries = new Map<number, MedicionQcDTO[]>();

        for (let i = 0; i < configsToLoad.length; i += BATCH_SIZE) {
          if (currentLoad.cancelled) return;

          const batch = configsToLoad.slice(i, i + BATCH_SIZE);
          const results = await Promise.all(
            batch.map(async (config) => {
              try {
                const mediciones = await getMeasurementsByConfiguration(config.Qcc_Id);
                return { config, mediciones: mediciones || [] };
              } catch (error) {
                return { config, mediciones: [] as MedicionQcDTO[] };
              }
            })
          );

          if (currentLoad.cancelled) return;

          // Acumular datos de mediciones del día para actualizar el grid
          const updatesForGrid = new Map<number, Map<string, any>>();

          for (const { config, mediciones } of results) {
            if (mediciones.length > 0) {
              newCacheEntries.set(config.Qcc_Id, mediciones);
            }

            const medicionHoy = mediciones.find((m: any) => {
              const fechaMedicion = new Date(ConvertLocalDate(m.Qcme_FechaHora));
              return fechaMedicion >= today && fechaMedicion < tomorrow;
            });

            if (medicionHoy) {
              const materialLoteKey = `${config.MaterialNombre}|${config.LoteCodigo}|${config.MaterialNivel}`;
              if (!updatesForGrid.has(config.Qcc_An_Id)) {
                updatesForGrid.set(config.Qcc_An_Id, new Map());
              }
              updatesForGrid.get(config.Qcc_An_Id)!.set(materialLoteKey, {
                Valor: medicionHoy.Qcme_Valor ?? null,
                ValorCualitativo: medicionHoy.Qcme_ValorCualitativo ?? null,
                Aprobado: medicionHoy.Qcme_Aprobado ?? null,
                AprobadoTexto: medicionHoy.AprobadoTexto ?? null,
                ResultadoTexto: medicionHoy.ResultadoTexto ?? null,
                Estado: 'saved',
                Qcme_Id: medicionHoy.Qcme_Id,
                Qcme_FechaHora: medicionHoy.Qcme_FechaHora,
                ZScore: medicionHoy.ZScore,
                Qcme_Estado: medicionHoy.Qcme_Estado,
                TieneViolaciones: medicionHoy.TieneViolaciones,
                Observacion: medicionHoy.Qcme_Observaciones,
                Nota: medicionHoy.Qcme_ComentarioValidacion ?? '',
              });
            }
          }

          // Actualizar el grid progresivamente con los datos de este lote
          if (updatesForGrid.size > 0) {
            setAnalitos((prev) =>
              prev.map((a) => {
                const analitoUpdates = updatesForGrid.get(a.Qcc_An_Id);
                if (!analitoUpdates) return a;
                const newControles = { ...a.controles };
                analitoUpdates.forEach((medData, mlKey) => {
                  if (newControles[mlKey]) {
                    newControles[mlKey] = { ...newControles[mlKey], ...medData };
                  }
                });
                return { ...a, controles: newControles };
              })
            );
            // DevExtreme no detecta cambios en el estado de React automáticamente.
            // Luego de que React procese el setAnalitos, forzar un reload del CustomStore
            // para que el grid muestre los valores cargados en background.
            setTimeout(() => {
              if (!currentLoad.cancelled) {
                gridRef.current?.instance.refresh();
              }
            }, 0);
          }
        }

        // Actualizar caché en batch al final
        if (newCacheEntries.size > 0 && !currentLoad.cancelled) {
          setMedicionesCache((prev) => {
            const newCache = new Map(prev);
            newCacheEntries.forEach((mediciones, configId) => {
              newCache.set(configId, mediciones);
            });
            return newCache;
          });
        }
      }
    } catch (error) {
      console.error('Error cargando analitos:', error);
      newToast('ERROR', 'Error al cargar analitos', 5);
      setIsLoading(false);
    }
  };

  const handleVerDetalle = async (rowData: any) => {
    const controlKeys = Object.keys(rowData.controles);
    if (controlKeys.length === 0) return;

    setIsLoadingDetail(true);

    const firstControl = rowData.controles[controlKeys[0]];

    setSelectedAnalitoId(rowData.Qcc_An_Id);
    setSelectedConfigId(firstControl.Qcc_Id);
    setSelectedAnalitoNombre(rowData.AnalitoNombre);

    // Cargar mediciones de TODOS los controles (usando caché)
    const newMedicionesCache = new Map<number, MedicionQcDTO[]>();
    const medicionesPromises = controlKeys.map(async (controlKey) => {
      const control = rowData.controles[controlKey];
      const [materialNombre, loteCodigo, materialNivel] = controlKey.split('|');

      // Usar el MaterialNivel directamente de la clave
      const controlName = materialNivel || materialNombre;

      try {
        // Verificar caché primero
        let mediciones = medicionesCache.get(control.Qcc_Id);
        if (!mediciones) {
          mediciones = await getMeasurementsByConfiguration(control.Qcc_Id);
          if (mediciones && mediciones.length > 0) {
            newMedicionesCache.set(control.Qcc_Id, mediciones);
          }
        }
        // Agregar el nombre del nivel a cada medición
        return (mediciones || []).map((m: any) => ({
          ...m,
          NivelNombre: controlName,
          MaterialNombre: materialNombre,
        }));
      } catch (error) {
        console.error(`Error cargando mediciones para ${controlName}:`, error);
        return [];
      }
    });

    const medicionesArrays = await Promise.all(medicionesPromises);

    // Batch update del caché de mediciones
    if (newMedicionesCache.size > 0) {
      setMedicionesCache((prev) => {
        const updated = new Map(prev);
        newMedicionesCache.forEach((v, k) => updated.set(k, v));
        return updated;
      });
    }

    const todasLasMediciones = medicionesArrays.flat();
    // Ordenar por fecha descendente
    todasLasMediciones.sort((a, b) => new Date(b.Qcme_FechaHora).getTime() - new Date(a.Qcme_FechaHora).getTime());
    setMedicionesAnalito(todasLasMediciones);

    // Cargar gráficos de TODOS los controles del analito (usando caché)
    const newGraficosCache = new Map<number, any>();
    const chartsPromises = controlKeys.map(async (controlKey) => {
      const control = rowData.controles[controlKey];
      const [materialNombre, loteCodigo, materialNivel] = controlKey.split('|');

      // Crear nombre completo: Material + Nivel
      const controlName = materialNivel ? `${materialNombre} - ${materialNivel}` : materialNombre;

      // Verificar caché primero
      let data = graficosCache.get(control.Qcc_Id);
      if (!data) {
        data = await getLeveyJenningsData(control.Qcc_Id);
        if (data) {
          newGraficosCache.set(control.Qcc_Id, data);
        }
      }
      return {
        controlKey,
        controlName,
        data: data || null,
      };
    });

    const chartsResults = await Promise.all(chartsPromises);

    // Batch update del caché de gráficos
    if (newGraficosCache.size > 0) {
      setGraficosCache((prev) => {
        const updated = new Map(prev);
        newGraficosCache.forEach((v, k) => updated.set(k, v));
        return updated;
      });
    }

    setChartsData(chartsResults);

    // Cambiar a la pestaña de detalle
    setSelectedTab(1);
    setIsLoadingDetail(false);
  };

  const handleRefresh = useCallback(() => {
    // Limpiar SIEMPRE la caché de configuraciones para reflejar cambios de estado
    setConfiguracionesCache(null);
    try { sessionStorage.removeItem(QC_CACHE_PREFIX + userLocation); } catch {}
    // Recargar el combobox con datos frescos de la API
    loadMaterialesLotes();

    if (selectedMaterialLotes && selectedMaterialLotes.length > 0) {
      // Limpiar caché de mediciones y recargar el grid
      setMedicionesCache(new Map());
      handleMaterialLoteChange(selectedMaterialLotes);
    }
  }, [selectedMaterialLotes, userLocation]);

  const renderEstadoCeldaMedicion = useCallback((cellData: any) => {
    const estado = cellData.value;
    if (!estado) return <div style={{ textAlign: 'center' }}>-</div>;

    let texto = '';
    let color = '';

    switch (estado) {
      case 1: // PENDIENTE
        texto = 'PENDIENTE';
        color = '#FFA500';
        break;
      case 2: // VALIDADO
        texto = 'VALIDADO';
        color = '#4CAF50';
        break;
      case 3: // RECHAZADO
        texto = 'RECHAZADO';
        color = '#F44336';
        break;
      case 4: // ALERTA
        texto = 'ALERTA';
        color = '#FF5722';
        break;
      default:
        texto = 'DESCONOCIDO';
        color = '#9E9E9E';
    }

    return (
      <div
        style={{
          backgroundColor: color,
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '11px',
        }}>
        {texto}
      </div>
    );
  }, []);

  const renderViolacionCelda = useCallback((cellData: any) => {
    if (!cellData.data.TieneViolaciones) {
      return <div style={{ textAlign: 'center' }}>-</div>;
    }

    return (
      <div
        style={{
          backgroundColor: '#FF5722',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '11px',
        }}>
        ⚠ SÍ
      </div>
    );
  }, []);

  const renderComentarioCelda = useCallback((cellData: any) => {
    const notaValidacion = cellData.data.Qcme_ComentarioValidacion;
    const observaciones = cellData.data.Qcme_Observaciones;
    const violaciones = cellData.data.Violaciones;

    if (cellData.data.TieneViolaciones && violaciones && violaciones.length > 0) {
      // Mostrar las reglas violadas
      const reglasVioladas = violaciones.map((v: any) => v.Qcv_Regla).join(', ');
      const descripcionCompleta = violaciones.map((v: any) => v.Qcv_Descripcion).join(' | ');

      return (
        <div style={{ padding: '4px' }}>
          <div style={{ fontWeight: 'bold', color: '#F44336', marginBottom: '4px' }}>Reglas violadas: {reglasVioladas}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>{descripcionCompleta}</div>
          {observaciones && <div style={{ marginTop: '4px', fontStyle: 'italic', borderTop: '1px solid #ddd', paddingTop: '4px' }}>{observaciones}</div>}
        </div>
      );
    }

    return <div style={{ padding: '4px' }}>{observaciones || '-'}</div>;
  }, []);

  const renderVerDetalleButton = useCallback((cellData: any) => {
    const rowData = cellData.data;
    return (
      <div style={{ textAlign: 'center' }}>
        <Button text='Ver Mediciones' onClick={() => handleVerDetalle(rowData)} stylingMode='contained' type='default' />
      </div>
    );
  }, []);

  // Abrir modal de nota
  const handleOpenNotaModal = useCallback((analitoId: number, materialLoteKey: string, notaActual: string) => {
    setNotaAnalitoId(analitoId);
    setNotaMaterialLoteKey(materialLoteKey);
    setNotaActual(notaActual || '');
    setNotaModalVisible(true);
  }, []);

  // Guardar nota
  const handleSaveNota = useCallback(async () => {
    if (notaAnalitoId && notaMaterialLoteKey) {
      // Primero actualizar el estado local
      setAnalitos((prev) =>
        prev.map((a) => {
          if (a.Qcc_An_Id === notaAnalitoId) {
            return {
              ...a,
              controles: {
                ...a.controles,
                [notaMaterialLoteKey]: {
                  ...a.controles[notaMaterialLoteKey],
                  Nota: notaActual,
                },
              },
            };
          }
          return a;
        })
      );

      // Buscar el control y persistir la nota si ya existe una medición del día
      const analito = analitos.find((a) => a.Qcc_An_Id === notaAnalitoId);
      if (analito) {
        const control = analito.controles[notaMaterialLoteKey];
        if (control) {
          // Verificar si ya existe una medición del día actual
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);

          const esMedicionHoy = control.Qcme_Id && control.Qcme_FechaHora && new Date(ConvertLocalDate(control.Qcme_FechaHora)) >= today && new Date(ConvertLocalDate(control.Qcme_FechaHora)) < tomorrow;

          const tipoRes: TipoResultadoQC = control.TipoResultado || 'CUANTITATIVO';
          const tieneValor = tipoRes === 'CUALITATIVO'
            ? (control.ValorCualitativo !== null && control.ValorCualitativo !== undefined)
            : (control.Valor !== null && control.Valor !== undefined);

          if (esMedicionHoy && control.Qcme_Id && tieneValor) {
            // Actualizar la medición existente con la nota
            try {
              const updateDto: any = {
                Qcme_Id: control.Qcme_Id,
                Qcme_Qcc_Id: control.Qcc_Id,
                Qcme_Us_Id: userId,
                Qcme_ComentarioValidacion: notaActual || undefined,
              };

              if (tipoRes === 'CUALITATIVO') {
                updateDto.Qcme_ValorCualitativo = control.ValorCualitativo;
              } else if (tipoRes === 'SEMICUANTITATIVO') {
                if (control.Valor != null) updateDto.Qcme_Valor = control.Valor;
                if (control.ValorCualitativo) updateDto.Qcme_ValorCualitativo = control.ValorCualitativo;
              } else {
                updateDto.Qcme_Valor = control.Valor;
              }

              const result = await updateMeasurement(updateDto);

              if (result) {
                // Invalidar caché de mediciones para esta configuración
                setMedicionesCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.delete(control.Qcc_Id);
                  return newCache;
                });
                newToast('SUCCESS', 'Nota guardada correctamente', 2);
              }
            } catch (error) {
              console.error('Error guardando nota:', error);
              newToast('ERROR', 'Error al guardar la nota', 5);
            }
          }
        }
      }

      setNotaModalVisible(false);
      setNotaActual('');
      setNotaAnalitoId(null);
      setNotaMaterialLoteKey('');

      // No es necesario hacer refresh, el estado ya se actualizó
    }
  }, [notaAnalitoId, notaMaterialLoteKey, notaActual, analitos, userId, updateMeasurement]);

  // Renderizar columna de nota
  const renderNotaColumn = useCallback(
    (materialLoteKey: string) => {
      return (cellData: any) => {
        const control = cellData.data.controles?.[materialLoteKey];
        if (!control) {
          return <div style={{ textAlign: 'center' }}>-</div>;
        }

        const tieneNota = control.Nota && control.Nota.trim() !== '';

        return (
          <div style={{ textAlign: 'center' }}>
            <Button icon={tieneNota ? resGridNotesCheck : resGridNotes} hint={tieneNota ? 'Ver/Editar nota' : 'Agregar nota'} onClick={() => handleOpenNotaModal(cellData.data.Qcc_An_Id, materialLoteKey, control.Nota || '')} stylingMode='text' type={tieneNota ? 'default' : 'normal'} />
          </div>
        );
      };
    },
    [handleOpenNotaModal]
  );

  // Set de materialLoteKeys que tienen al menos un analito SEMICUANTITATIVO.
  // Derivado de configuracionesMap (estable tras la selección) para evitar
  // que renderDynamicColumns dependa de `analitos` y recree las columnas
  // en cada guardado de medición, lo que causaba el salto visual del grid.
  const semicuantitativoKeys = useMemo(() => {
    const set = new Set<string>();
    configuracionesMap.forEach((config) => {
      if (config.Qcc_TipoResultado === 'SEMICUANTITATIVO') {
        const mlKey = `${config.MaterialNombre}|${config.LoteCodigo}|${config.MaterialNivel}`;
        set.add(mlKey);
      }
    });
    return set;
  }, [configuracionesMap]);

  // Generar columnas dinámicas según los controles seleccionados
  const renderDynamicColumns = useMemo(() => {
    if (!selectedMaterialLotes || selectedMaterialLotes.length === 0) {
      return null;
    }

    // Ordenar los materiales-lotes por nivel (NIVEL 1, NIVEL 2, NIVEL 3)
    const sortedMaterialLotes = [...selectedMaterialLotes].sort((a, b) => {
      // Extraer el MaterialNivel desde la clave directamente
      const nivelA = a.split('|')[2] || '';
      const nivelB = b.split('|')[2] || '';

      // Extraer el número del nivel (NIVEL 1 -> 1, NIVEL 2 -> 2, etc.)
      const nivelNumA = nivelA.match(/\d+/)?.[0] || '999';
      const nivelNumB = nivelB.match(/\d+/)?.[0] || '999';

      return parseInt(nivelNumA) - parseInt(nivelNumB);
    });

    return sortedMaterialLotes.map((materialLoteKey) => {
      const [materialNombre, loteCodigo, materialNivel] = materialLoteKey.split('|');

      // Crear el título de la columna agrupada: Material + Nivel
      const groupCaption = materialNivel ? `${materialNombre} - ${materialNivel}` : materialNombre;

      return (
        <Column key={`group_${materialLoteKey}`} caption={groupCaption} alignment='center'>
          <Column
            key={`valor_${materialLoteKey}`}
            caption='VALOR'
            dataField={`Valor_${materialLoteKey}`}
            width={120}
            alignment='center'
            allowEditing={true}
            calculateCellValue={(rowData: any) => {
              const ctrl = rowData.controles?.[materialLoteKey];
              if (!ctrl) return null;
              const tipo = ctrl.TipoResultado || 'CUANTITATIVO';
              if (tipo === 'CUALITATIVO') return ctrl.ValorCualitativo ?? null;
              return ctrl.Valor ?? null;
            }}
            setCellValue={(newData: any, value: any) => {
              if (!newData.controles) newData.controles = {};
              if (!newData.controles[materialLoteKey]) {
                newData.controles[materialLoteKey] = {};
              }
              newData[`Valor_${materialLoteKey}`] = value;
            }}
            cellRender={(cellData: any) => {
              const control = cellData.data.controles?.[materialLoteKey];
              if (!control) {
                return <div style={{ textAlign: 'center', color: '#ccc', fontStyle: 'italic' }}>N/A</div>;
              }
              const tipoRes = control.TipoResultado || 'CUANTITATIVO';
              if (tipoRes === 'CUALITATIVO') {
                const valCual = control.ValorCualitativo;
                if (!valCual) return <div style={{ textAlign: 'center' }}></div>;
                const aprobado = control.Aprobado;
                const color = aprobado === true ? '#4CAF50' : aprobado === false ? '#F44336' : '#9E9E9E';
                return (
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ backgroundColor: color, color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                      {valCual}
                    </span>
                  </div>
                );
              }
              const valor = control.Valor;
              if (valor === null || valor === undefined) {
                return <div style={{ textAlign: 'center' }}></div>;
              }
              return <div style={{ textAlign: 'center' }}>{typeof valor === 'number' ? valor.toFixed(2) : valor}</div>;
            }}
          />
          {semicuantitativoKeys.has(materialLoteKey) && (
            <Column
              key={`cualit_${materialLoteKey}`}
              caption='CUALIT.'
              dataField={`ValorCual_${materialLoteKey}`}
              width={120}
              alignment='center'
              allowEditing={true}
              calculateCellValue={(rowData: any) => {
                const ctrl = rowData.controles?.[materialLoteKey];
                if (!ctrl || ctrl.TipoResultado !== 'SEMICUANTITATIVO') return null;
                return ctrl.ValorCualitativo ?? null;
              }}
              setCellValue={(newData: any, value: any) => {
                if (!newData.controles) newData.controles = {};
                if (!newData.controles[materialLoteKey]) {
                  newData.controles[materialLoteKey] = {};
                }
                newData[`ValorCual_${materialLoteKey}`] = value;
              }}
              cellRender={(cellData: any) => {
                const control = cellData.data.controles?.[materialLoteKey];
                if (!control || control.TipoResultado !== 'SEMICUANTITATIVO') {
                  return <div style={{ textAlign: 'center' }}>-</div>;
                }
                const valCual = control.ValorCualitativo;
                if (!valCual) return <div style={{ textAlign: 'center' }}></div>;
                const aprobado = control.Aprobado;
                const color = aprobado === true ? '#4CAF50' : aprobado === false ? '#F44336' : '#9E9E9E';
                return (
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ backgroundColor: color, color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                      {valCual}
                    </span>
                  </div>
                );
              }}
            />
          )}
          <Column
            key={`media_${materialLoteKey}`}
            caption='MEDIA'
            width={100}
            alignment='center'
            allowEditing={false}
            calculateCellValue={(rowData: any) => {
              const ctrl = rowData.controles?.[materialLoteKey];
              if (!ctrl || ctrl.TipoResultado === 'CUALITATIVO') return null;
              return ctrl.Qcc_MediaEsperada ?? null;
            }}
            cellRender={(cellData: any) => {
              const control = cellData.data.controles?.[materialLoteKey];
              if (!control) {
                return <div style={{ textAlign: 'center', color: '#ccc', fontStyle: 'italic' }}>N/A</div>;
              }
              if (control.TipoResultado === 'CUALITATIVO') {
                return <div style={{ textAlign: 'center' }}>-</div>;
              }
              const media = control.Qcc_MediaEsperada;
              if (media === null || media === undefined) {
                return <div style={{ textAlign: 'center' }}>-</div>;
              }
              return <div style={{ textAlign: 'center' }}>{media.toFixed(2)}</div>;
            }}
          />
          <Column key={`nota_${materialLoteKey}`} caption='NOTA' width={80} alignment='center' allowEditing={false} cellRender={renderNotaColumn(materialLoteKey)} allowFiltering={false} allowSorting={false} />
        </Column>
      );
    });
  }, [selectedMaterialLotes, semicuantitativoKeys, configuracionesMap, renderNotaColumn]);

  if (!Prm_QC_R) {
    return (
      <div className='view crm-contact-list'>
        <div className='view-wrapper'>
          <h3>No tiene permisos para acceder a este módulo</h3>
        </div>
      </div>
    );
  }

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-masive-measurements-qc'>
        <ScrollView width='100%' height='100%' showScrollbar='onScroll'>
          <div className='measurements-header'>
            {/* Título y Selector en la misma línea */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '8px' }}>
              <h2 style={{ margin: 0 }}>Ingreso Masivo de Mediciones</h2>
              <div className='selector-group'>
                <TagBox
                  dataSource={materialesLotes}
                  displayExpr='DisplayText'
                  valueExpr='MaterialLote'
                  value={selectedMaterialLotes}
                  onValueChanged={(e) => {
                    if (e.value) {
                      handleMaterialLoteChange(e.value);
                    } else {
                      handleMaterialLoteChange([]);
                    }
                  }}
                  placeholder={isLoadingControles ? 'Cargando controles...' : 'Seleccione uno o varios controles...'}
                  disabled={isLoadingControles}
                  searchEnabled={true}
                  searchExpr={['MaterialNombre', 'MaterialNivel', 'LoteCodigo', 'DisplayText']}
                  showClearButton={true}
                  showSelectionControls={true}
                  applyValueMode='useButtons'
                  width={600}
                  showMultiTagOnly={false}
                  maxDisplayedTags={1}
                  deferRendering={true}
                />
              </div>
            </div>

            {/* Información del control seleccionado */}
            {selectedMaterialLotes && selectedMaterialLotes.length > 0 && analitos.length > 0 && (
              <div className='qc-stats-row'>
                <div className='qc-stat-badge' style={{ '--badge-color': '#007bff' } as React.CSSProperties}>
                  <div className='qc-stat-icon'><i className='dx-icon-group' /></div>
                  <div className='qc-stat-info'><span className='qc-stat-value'>{selectedMaterialLotes.length}</span><span className='qc-stat-label'>Controles</span></div>
                </div>
                <div className='qc-stat-badge' style={{ '--badge-color': '#fd7e14' } as React.CSSProperties}>
                  <div className='qc-stat-icon'><i className='dx-icon-contentlayout' /></div>
                  <div className='qc-stat-info'><span className='qc-stat-value'>{analitos.length}</span><span className='qc-stat-label'>Analitos</span></div>
                </div>
                <div className='qc-stat-badge' style={{ '--badge-color': '#28a745' } as React.CSSProperties}>
                  <div className='qc-stat-icon'><i className='dx-icon-check' /></div>
                  <div className='qc-stat-info'><span className='qc-stat-value'>{analitos.reduce((count, a) => {
                    return count + Object.values(a.controles || {}).filter((c: any) => c.Estado === 'saved').length;
                  }, 0)}</span><span className='qc-stat-label'>Registrados</span></div>
                </div>
                <div className='qc-stat-badge' style={{ '--badge-color': '#ffc107' } as React.CSSProperties}>
                  <div className='qc-stat-icon'><i className='dx-icon-clock' /></div>
                  <div className='qc-stat-info'><span className='qc-stat-value'>{analitos.reduce((count, a) => {
                    return count + Object.values(a.controles || {}).filter((c: any) => !c.Estado || c.Estado === 'pending').length;
                  }, 0)}</span><span className='qc-stat-label'>Pendientes</span></div>
                </div>
              </div>
            )}
          </div>

          {/* Tab Panel: Ingreso y Gráficos */}
          <TabPanel selectedIndex={selectedTab} onSelectedIndexChange={setSelectedTab} animationEnabled={true} swipeEnabled={false} style={{ flex: 1, minHeight: 0 }}>
            {/* Pestaña 1: Ingreso de Mediciones */}
            <TabItem title='MEDICIONES'>
              <div className='masive-grid-container' style={{ position: 'relative' }}>
                <LoadPanel
                  visible={isLoading}
                  message='Cargando mediciones...'
                  showIndicator={true}
                  showPane={true}
                  shading={true}
                  shadingColor='rgba(255,255,255,0.7)'
                  position={{ of: '.masive-grid-container' }}
                />
                <Toolbar>
                  <ToolbarItem location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                    <Button id='ActualizarMasivo' icon={resButtonRefresh} text='Actualizar' stylingMode='text' onClick={handleRefresh} />
                    <Tooltip target={'#ActualizarMasivo'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                      Refrescar lista de analitos
                    </Tooltip>
                  </ToolbarItem>
                </Toolbar>
                <DataGrid
                  className='grid'
                  dataSource={analitoDataSource}
                  ref={gridRef}
                  width={'100%'}
                  height={'100%'}
                  hoverStateEnabled={true}
                  repaintChangesOnly={true}
                  highlightChanges={true}
                  rowAlternationEnabled={true}
                  showColumnLines={true}
                  disabled={!selectedMaterialLotes || selectedMaterialLotes.length === 0 || !Prm_QC_C}
                  onEditorPreparing={(e) => {
                    if (!e.dataField) return;

                    let materialLoteKey: string | null = null;
                    let isQualitativeCol = false;

                    if (e.dataField.startsWith('ValorCual_')) {
                      materialLoteKey = e.dataField.replace('ValorCual_', '');
                      isQualitativeCol = true;
                    } else if (e.dataField.startsWith('Valor_')) {
                      materialLoteKey = e.dataField.replace('Valor_', '');
                    }

                    if (!materialLoteKey) return;

                    const control = e.row?.data?.controles?.[materialLoteKey];

                    if (!control) {
                      e.editorOptions.disabled = true;
                      return;
                    }

                    const tipoRes: TipoResultadoQC = control.TipoResultado || 'CUANTITATIVO';

                    // Para columna CUALIT., deshabilitar si no es SEMICUANTITATIVO
                    if (isQualitativeCol && tipoRes !== 'SEMICUANTITATIVO') {
                      e.editorOptions.disabled = true;
                      return;
                    }

                    // Para columna VALOR con tipo CUALITATIVO, usar SelectBox
                    if (!isQualitativeCol && tipoRes === 'CUALITATIVO') {
                      const textoItems = (control.ValoresEsperados || [])
                        .filter((v: any) => v.Qcve_TipoValor === 'TEXTO')
                        .map((v: any) => v.Qcve_ValorTexto || v.ValorEsperadoTexto)
                        .filter(Boolean);

                      e.editorName = 'dxSelectBox';
                      e.editorOptions.dataSource = textoItems;
                      e.editorOptions.searchEnabled = true;
                      e.editorOptions.acceptCustomValue = true;
                    }

                    // Para columna CUALIT. con SEMICUANTITATIVO, usar SelectBox
                    if (isQualitativeCol && tipoRes === 'SEMICUANTITATIVO') {
                      const textoItems = (control.ValoresEsperados || [])
                        .filter((v: any) => v.Qcve_TipoValor === 'TEXTO')
                        .map((v: any) => v.Qcve_ValorTexto || v.ValorEsperadoTexto)
                        .filter(Boolean);

                      e.editorName = 'dxSelectBox';
                      e.editorOptions.dataSource = textoItems;
                      e.editorOptions.searchEnabled = true;
                      e.editorOptions.acceptCustomValue = true;
                    }

                    // Para CUANTITATIVO/SEMICUANTITATIVO en columna VALOR, usar NumberBox
                    if (!isQualitativeCol && tipoRes !== 'CUALITATIVO') {
                      e.editorName = 'dxNumberBox';
                      e.editorOptions.format = '#,##0.00';
                    }

                    // Solo permitir editar valores del día actual
                    if (control.Qcme_FechaHora) {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const tomorrow = new Date(today);
                      tomorrow.setDate(tomorrow.getDate() + 1);

                      const fechaMedicion = new Date(ConvertLocalDate(control.Qcme_FechaHora));
                      const esMedicionHoy = fechaMedicion >= today && fechaMedicion < tomorrow;

                      if (!esMedicionHoy) {
                        e.editorOptions.disabled = true;
                        e.editorOptions.hint = 'Solo se pueden modificar mediciones del día actual';
                      }
                    }
                  }}>
                  <Editing mode='cell' allowUpdating={true} selectTextOnEditStart={true} startEditAction='click' />
                  <KeyboardNavigation editOnKeyPress={true} enterKeyAction='moveFocus' enterKeyDirection='column' />
                  <HeaderFilter visible={true} />
                  <Scrolling mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
                  <Sorting mode='multiple' />
                  <Selection mode='none' />
                  <StateStoring enabled={true} type='custom' />

                  {/* Columna fija de Analito */}
                  <Column caption='ANALITO' dataField='AnalitoNombre' alignment='left' allowEditing={false} fixed={true} sortOrder='asc' />

                  {/* Columnas dinámicas según controles seleccionados */}
                  {renderDynamicColumns}

                  {/* Columna de detalle */}
                  <Column caption='DETALLE' width={200} alignment='center' allowEditing={false} cellRender={renderVerDetalleButton} allowFiltering={false} allowSorting={false} />
                </DataGrid>
              </div>
            </TabItem>

            {/* Pestaña 2: Detalle del Analito */}
            <TabItem title='DETALLE DEL ANALITO'>
              {selectedConfigId ? (
                <div className='analito-detail-container' style={{ position: 'relative' }}>
                  <LoadPanel
                    visible={isLoadingDetail}
                    message='Cargando gráficos...'
                    showIndicator={true}
                    showPane={true}
                    shading={true}
                    shadingColor='rgba(255,255,255,0.7)'
                    position={{ of: '.analito-detail-container' }}
                  />
                  <TabPanel className='sub-tab-pills' animationEnabled={true} swipeEnabled={false} height='100%' style={{ marginTop: '8px' }}>
                    {/* Sub-tab: Gráficos */}
                    <TabItem title='Gráficos Levey-Jennings'>
                  <ScrollView width='100%' height='100%'>
                  <div className='charts-section' style={{ padding: '5px 0' }}>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '2px' }}>
                      <span className='analito-badge'>{selectedAnalitoNombre}</span>
                      {chartsData && chartsData.length > 1 && <CheckBox text='Ver niveles juntos' value={nivelesJuntos} onValueChanged={(e) => setNivelesJuntos(e.value)} />}
                      <CheckBox text='Mostrar líneas' value={mostrarLineas} onValueChanged={(e) => setMostrarLineas(e.value)} />
                    </div>
                    {chartsData && chartsData.length > 0 ? (
                      nivelesJuntos ? (
                        // Vista combinada: todos los niveles en un solo gráfico
                        <div className='chart-section' style={{ marginBottom: '30px' }}>
                          {(() => {
                            // Preparar datos con una serie por nivel
                            const maxMediciones = Math.max(...chartsData.map((c) => c.data?.Mediciones?.length || 0));
                            const combinedData: any[] = [];

                            for (let i = 0; i < maxMediciones; i++) {
                              const dataPoint: any = { NumeroMedicion: i + 1 };

                              chartsData.forEach((chartInfo, chartIndex) => {
                                if (chartInfo.data && chartInfo.data.Mediciones && chartInfo.data.Mediciones[i]) {
                                  const m = chartInfo.data.Mediciones[i];
                                  dataPoint[`Valor_${chartIndex}`] = m.Valor;
                                  dataPoint[`Fecha_${chartIndex}`] = m.FechaHora;
                                  dataPoint[`Usuario_${chartIndex}`] = m.UsuarioNombre || 'N/A';
                                  dataPoint[`Estado_${chartIndex}`] = m.Estado;
                                  // Determinar color: si tiene estado definitivo, priorizar estado sobre ColorPunto del backend
                                  const colorByEstado = m.Estado === 2 ? '#4CAF50' : m.Estado === 3 ? '#F44336' : m.Estado === 4 ? '#FF5722' : null;
                                  dataPoint[`ColorPunto_${chartIndex}`] = colorByEstado || m.ColorPunto || '#2196F3';
                                }
                              });

                              // Agregar líneas de referencia del primer nivel
                              if (chartsData[0] && chartsData[0].data) {
                                dataPoint.Media_Ref = chartsData[0].data.Media;
                                dataPoint.SD2Positivo_Ref = chartsData[0].data.Linea_2SD_Superior;
                                dataPoint.SD2Negativo_Ref = chartsData[0].data.Linea_2SD_Inferior;
                              }

                              combinedData.push(dataPoint);
                            }

                            const colors = ['#2196F3', '#FF9800', '#4CAF50', '#E91E63', '#9C27B0'];

                            return (
                              <Chart
                                dataSource={combinedData}
                                height={450}
                                customizePoint={(pointInfo: any) => {
                                  // Aplicar ColorPunto del backend solo a las series de mediciones
                                  if (pointInfo.seriesName && !pointInfo.seriesName.includes('Ref') && !pointInfo.seriesName.includes('SD') && !pointInfo.seriesName.includes('Media')) {
                                    const chartIndex = chartsData.findIndex((c) => c.controlName === pointInfo.seriesName);
                                    if (chartIndex >= 0 && pointInfo.point && pointInfo.point.data) {
                                      const colorPunto = pointInfo.point.data[`ColorPunto_${chartIndex}`];
                                      if (colorPunto) {
                                        return {
                                          color: colorPunto,
                                          hoverStyle: {
                                            color: colorPunto,
                                          },
                                        };
                                      }
                                    }
                                  }
                                  return {};
                                }}>
                                <ArgumentAxis title='Número de Medición' />
                                <ValueAxis title='Valor' />
                                <Animation enabled={true} duration={800} easing='easeOutCubic' />
                                <CommonSeriesSettings argumentField='NumeroMedicion' />

                                {/* Líneas de referencia - Declaradas primero para que queden debajo */}
                                {chartsData.length > 0 && chartsData[0].data && (
                                  <>
                                    <Series valueField='Media_Ref' name='Media (Ref)' color='#66BB6A' type='line' dashStyle='longDash' width={2} opacity={0.6}>
                                      <Point visible={false} />
                                    </Series>
                                    <Series valueField='SD2Positivo_Ref' name='+2 SD (Ref)' color='#FFB74D' type='line' dashStyle='dash' width={1.2} opacity={0.5}>
                                      <Point visible={false} />
                                    </Series>
                                    <Series valueField='SD2Negativo_Ref' name='-2 SD (Ref)' color='#FFB74D' type='line' dashStyle='dash' width={1.2} opacity={0.5}>
                                      <Point visible={false} />
                                    </Series>
                                  </>
                                )}

                                {/* Serie de mediciones para cada nivel - Declaradas al final para que queden encima */}
                                {chartsData.map((chartInfo, chartIndex) => (
                                  <Series key={`mediciones-${chartIndex}`} valueField={`Valor_${chartIndex}`} name={chartInfo.controlName} color={colors[chartIndex % colors.length]} type={mostrarLineas ? 'line' : 'scatter'} width={mostrarLineas ? 1.5 : 0} hoverMode='onlyPoint'>
                                    <Point size={12} visible={true} hoverMode='onlyPoint' symbol='circle' />
                                  </Series>
                                ))}

                                <Legend verticalAlignment='bottom' horizontalAlignment='center' />
                                <ChartTooltip
                                  enabled={true}
                                  shared={false}
                                  zIndex={10000}
                                  customizeTooltip={(pointInfo: any) => {
                                    if (pointInfo.seriesName && !pointInfo.seriesName.includes('Ref') && !pointInfo.seriesName.includes('SD')) {
                                      const chartIndex = chartsData.findIndex((c) => c.controlName === pointInfo.seriesName);
                                      if (chartIndex >= 0) {
                                        const fecha = pointInfo.point.data[`Fecha_${chartIndex}`];
                                        const usuario = pointInfo.point.data[`Usuario_${chartIndex}`];
                                        const nota = pointInfo.point.data[`Nota_${chartIndex}`];
                                        let fechaFormateada = 'N/A';

                                        if (fecha) {
                                          const fechaObj = new Date(fecha);
                                          fechaFormateada = fechaObj.toLocaleString('es-ES', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          });
                                        }

                                        return {
                                          html: `
                                            <div style="padding: 8px;">
                                              <div><strong>Nivel:</strong> ${pointInfo.seriesName}</div>
                                              <div><strong>Valor:</strong> ${pointInfo.valueText}</div>
                                              <div><strong>Fecha:</strong> ${fechaFormateada}</div>
                                              <div><strong>Usuario:</strong> ${usuario}</div>
                                              <div><strong>Nota:</strong> ${nota}</div>
                                            </div>
                                          `,
                                        };
                                      }
                                    }
                                    return { text: pointInfo.valueText };
                                  }}
                                />
                              </Chart>
                            );
                          })()}
                        </div>
                      ) : (
                        // Vista separada: un gráfico por nivel
                        chartsData.map((chartInfo, index) => {
                          // Calcular estadísticas para el panel lateral
                          const totalMed = chartInfo.data?.TotalMediciones || 0;
                          const medValidadas = chartInfo.data?.MedicionesValidadas || 0;
                          const medConViolaciones = chartInfo.data?.MedicionesConViolaciones || 0;
                          const medPendientes = totalMed - medValidadas - medConViolaciones;
                          const conformidad = totalMed > 0 ? ((medValidadas / totalMed) * 100).toFixed(1) : '0.0';
                          const media = chartInfo.data?.Media || 0;
                          const sd = chartInfo.data?.DesviacionEstandar || 0;
                          const cv = media > 0 ? ((sd / media) * 100).toFixed(2) : '0.00';
                          const chartTipoRes = chartInfo.data?.TipoResultado || 'CUANTITATIVO';
                          const esCuantChart = chartTipoRes !== 'CUALITATIVO';
                          const medAprobadas = chartInfo.data?.MedicionesAprobadas || 0;
                          const medNoAprobadas = chartInfo.data?.MedicionesNoAprobadas || 0;
                          const pctAprobacion = totalMed > 0 ? (medAprobadas / totalMed) * 100 : 0;

                          return (
                          <div key={chartInfo.controlKey} className='chart-with-panel' style={{ marginBottom: '15px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '5px' }}>
                              <h5 style={{ margin: 0, color: '#333', fontSize: '14px' }}>{chartInfo.controlName}</h5>
                              {chartInfo.data?.TEa != null && chartInfo.data?.SigmaActual != null && (
                                <span className='sigma-inline-chip' style={{ backgroundColor: getSigmaColor(chartInfo.data.ClasificacionSigma) }}>
                                  σ {chartInfo.data.SigmaActual.toFixed(2)} · {getSigmaLabel(chartInfo.data.ClasificacionSigma)}
                                </span>
                              )}
                            </div>
                            <div className='chart-layout-row'>
                              {/* Gráfico - lado izquierdo */}
                              <div className='chart-area'>
                            {!chartInfo.data || !chartInfo.data.Mediciones || chartInfo.data.Mediciones.length === 0 ? (
                              <div className='no-chart-data'>
                                <p>No hay datos suficientes para generar el gráfico de {chartInfo.controlName}</p>
                              </div>
                            ) : !esCuantChart ? (
                              <Chart
                                dataSource={chartInfo.data.Mediciones.map((m: any, idx: number) => ({
                                  NumeroMedicion: idx + 1,
                                  Aprobado: m.Aprobado === true ? 1 : 0,
                                  ValorCualitativo: m.ValorCualitativo || '',
                                  Fecha: m.FechaHora,
                                  Usuario: m.UsuarioNombre || 'N/A',
                                }))}
                                height={400}
                                customizePoint={(pointInfo: any) => {
                                  if (pointInfo.seriesName === 'Resultado') {
                                    const aprobado = pointInfo.data.Aprobado;
                                    const color = aprobado === 1 ? '#4CAF50' : '#F44336';
                                    return { color, hoverStyle: { color } };
                                  }
                                  return {};
                                }}>
                                <ArgumentAxis title='Número de Medición' />
                                <ValueAxis title='' visualRange={[-0.5, 1.5]} />
                                <Animation enabled={true} duration={800} easing='easeOutCubic' />
                                <CommonSeriesSettings argumentField='NumeroMedicion' />
                                <Series valueField='Aprobado' name='Resultado' type='scatter'>
                                  <Point size={14} visible={true} symbol='circle' />
                                </Series>
                                <Legend verticalAlignment='bottom' horizontalAlignment='center' />
                                <ChartTooltip
                                  enabled={true}
                                  shared={false}
                                  zIndex={10000}
                                  customizeTooltip={(pointInfo: any) => {
                                    if (pointInfo.seriesName === 'Resultado') {
                                      const d = pointInfo.point.data;
                                      let fechaFormateada = 'N/A';
                                      if (d.Fecha) {
                                        fechaFormateada = new Date(d.Fecha).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                                      }
                                      return { html: `<div style="padding: 8px;"><div><strong>Valor:</strong> ${d.ValorCualitativo}</div><div><strong>Resultado:</strong> ${d.Aprobado === 1 ? 'APROBADO' : 'NO APROBADO'}</div><div><strong>Fecha:</strong> ${fechaFormateada}</div><div><strong>Usuario:</strong> ${d.Usuario}</div></div>` };
                                    }
                                    return { text: pointInfo.valueText };
                                  }}
                                />
                              </Chart>
                            ) : (
                              <Chart
                                dataSource={(() => {
                                  const dataPoints = chartInfo.data.Mediciones.map((m: any, idx: number) => {
                                    // Determinar color: si tiene estado definitivo, priorizar estado sobre ColorPunto del backend
                                    const colorByEstado = m.Estado === 2 ? '#4CAF50' : m.Estado === 3 ? '#F44336' : m.Estado === 4 ? '#FF5722' : null;
                                    return {
                                      NumeroMedicion: idx + 1,
                                      Valor: m.Valor,
                                      Media: chartInfo.data.Media,
                                      SD1Positivo: chartInfo.data.Linea_1SD_Superior,
                                      SD1Negativo: chartInfo.data.Linea_1SD_Inferior,
                                      SD2Positivo: chartInfo.data.Linea_2SD_Superior,
                                      SD2Negativo: chartInfo.data.Linea_2SD_Inferior,
                                      SD3Positivo: chartInfo.data.Linea_3SD_Superior,
                                      SD3Negativo: chartInfo.data.Linea_3SD_Inferior,
                                      ColorPunto: colorByEstado || m.ColorPunto || '#2196F3',
                                      Fecha: m.FechaHora,
                                      Usuario: m.UsuarioNombre || 'N/A',
                                    };
                                  });

                                  if (dataPoints.length === 1) {
                                    const lineaData = {
                                      Valor: null,
                                      Media: chartInfo.data.Media,
                                      SD1Positivo: chartInfo.data.Linea_1SD_Superior,
                                      SD1Negativo: chartInfo.data.Linea_1SD_Inferior,
                                      SD2Positivo: chartInfo.data.Linea_2SD_Superior,
                                      SD2Negativo: chartInfo.data.Linea_2SD_Inferior,
                                      SD3Positivo: chartInfo.data.Linea_3SD_Superior,
                                      SD3Negativo: chartInfo.data.Linea_3SD_Inferior,
                                      ColorPunto: null,
                                      Fecha: null,
                                      Usuario: null,
                                    };
                                    for (let i = 5; i > 0; i--) {
                                      dataPoints.unshift({ NumeroMedicion: -i, ...lineaData });
                                    }
                                    dataPoints[5].NumeroMedicion = 0;
                                    for (let i = 1; i <= 5; i++) {
                                      dataPoints.push({ NumeroMedicion: i, ...lineaData });
                                    }
                                  }
                                  return dataPoints;
                                })()}
                                height={400}
                                customizePoint={(pointInfo: any) => {
                                  if (pointInfo.seriesName === 'Mediciones') {
                                    const colorPunto = pointInfo.data.ColorPunto;
                                    if (colorPunto) {
                                      return { color: colorPunto, hoverStyle: { color: colorPunto } };
                                    }
                                  }
                                  return {};
                                }}>
                                <ArgumentAxis title='Número de Medición' />
                                <ValueAxis title='Valor' />
                                <Animation enabled={true} duration={800} easing='easeOutCubic' />
                                <CommonSeriesSettings argumentField='NumeroMedicion' />
                                <Series valueField='Media' name='Media' color='#66BB6A' type='line' dashStyle='longDash' width={2} opacity={0.6}><Point visible={false} /></Series>
                                <Series valueField='SD1Positivo' name='+1 SD' color='#FFD54F' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
                                <Series valueField='SD1Negativo' name='-1 SD' color='#FFD54F' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
                                <Series valueField='SD2Positivo' name='+2 SD' color='#FFB74D' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
                                <Series valueField='SD2Negativo' name='-2 SD' color='#FFB74D' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
                                <Series valueField='SD3Positivo' name='+3 SD' color='#EF9A9A' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
                                <Series valueField='SD3Negativo' name='-3 SD' color='#EF9A9A' type='line' dashStyle='dash' width={1.2} opacity={0.5}><Point visible={false} /></Series>
                                <Series valueField='Valor' name='Mediciones' color='#2196F3' type={mostrarLineas ? 'line' : 'scatter'} width={mostrarLineas ? 1.5 : 0} hoverMode='onlyPoint'>
                                  <Point size={12} visible={true} hoverMode='onlyPoint' symbol='circle' />
                                </Series>
                                <Legend verticalAlignment='bottom' horizontalAlignment='center' />
                                <ChartTooltip
                                  enabled={true}
                                  shared={false}
                                  zIndex={10000}
                                  customizeTooltip={(pointInfo: any) => {
                                    if (pointInfo.seriesName === 'Mediciones') {
                                      const fecha = pointInfo.point.data.Fecha;
                                      const usuario = pointInfo.point.data.Usuario;
                                      let fechaFormateada = 'N/A';
                                      if (fecha) {
                                        const fechaObj = new Date(fecha);
                                        fechaFormateada = fechaObj.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                                      }
                                      return { html: `<div style="padding: 8px;"><div><strong>Valor:</strong> ${pointInfo.valueText}</div><div><strong>Fecha:</strong> ${fechaFormateada}</div><div><strong>Usuario:</strong> ${usuario}</div></div>` };
                                    }
                                    return { text: pointInfo.valueText };
                                  }}
                                />
                              </Chart>
                            )}
                              </div>

                              {/* Panel lateral - Estado del Control + Límites */}
                              <div className='chart-side-panel'>
                                {esCuantChart ? (
                                  <>
                                    <div className='side-panel-card'>
                                      <div className='side-panel-title'>⚙ Estado del Control</div>
                                      <div className='conformidad-valor' style={{ color: parseFloat(conformidad) >= 80 ? '#4CAF50' : parseFloat(conformidad) >= 50 ? '#FF9800' : '#F44336' }}>
                                        {conformidad}%
                                      </div>
                                      <div className='conformidad-label'>Aceptabilidad</div>
                                      <div className='estado-items'>
                                        <div className='estado-item'><span className='estado-dot' style={{ background: '#4CAF50' }}></span><span>Aceptados</span><strong>{medValidadas}</strong></div>
                                        <div className='estado-item'><span className='estado-dot' style={{ background: '#F44336' }}></span><span>Rechazados</span><strong>{medConViolaciones}</strong></div>
                                        <div className='estado-item'><span className='estado-dot' style={{ background: '#FFA500' }}></span><span>Pendientes</span><strong>{medPendientes < 0 ? 0 : medPendientes}</strong></div>
                                      </div>
                                    </div>
                                    <div className='side-panel-card'>
                                      <div className='side-panel-title'>⚠ Límites de Control</div>
                                      <div className='limites-grid'>
                                        <div className='limite-row'><span>Media:</span><strong>{media.toFixed(2)}</strong></div>
                                        <div className='limite-row'><span>SD:</span><strong>{sd.toFixed(2)}</strong></div>
                                        <div className='limite-row'><span>CV:</span><strong>{cv}%</strong></div>
                                        <div className='limite-row sd-row' style={{ color: '#FFC107' }}><span>±1SD:</span><strong>{chartInfo.data?.Linea_1SD_Inferior?.toFixed(2)} - {chartInfo.data?.Linea_1SD_Superior?.toFixed(2)}</strong></div>
                                        <div className='limite-row sd-row' style={{ color: '#FF9800' }}><span>±2SD:</span><strong>{chartInfo.data?.Linea_2SD_Inferior?.toFixed(2)} - {chartInfo.data?.Linea_2SD_Superior?.toFixed(2)}</strong></div>
                                        <div className='limite-row sd-row' style={{ color: '#F44336' }}><span>±3SD:</span><strong>{chartInfo.data?.Linea_3SD_Inferior?.toFixed(2)} - {chartInfo.data?.Linea_3SD_Superior?.toFixed(2)}</strong></div>
                                      </div>
                                    </div>
                                    {chartInfo.data?.MedicionesConViolaciones > 0 ? (
                                      <div className='side-panel-card violations'>
                                        <div className='violation-icon'>⚠</div>
                                        <div className='violation-text' style={{ color: '#F44336' }}>{chartInfo.data.MedicionesConViolaciones} Violación(es)</div>
                                      </div>
                                    ) : (
                                      <div className='side-panel-card no-violations'>
                                        <div className='violation-icon'>✓</div>
                                        <div className='violation-text' style={{ color: '#4CAF50' }}>Sin Violaciones</div>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <div className='side-panel-card'>
                                      <div className='side-panel-title'>⚙ Estado del Control</div>
                                      <div className='conformidad-valor' style={{ color: pctAprobacion >= 80 ? '#4CAF50' : pctAprobacion >= 50 ? '#FF9800' : '#F44336' }}>
                                        {pctAprobacion.toFixed(1)}%
                                      </div>
                                      <div className='conformidad-label'>Aprobación</div>
                                      <div className='estado-items'>
                                        <div className='estado-item'><span className='estado-dot' style={{ background: '#4CAF50' }}></span><span>Aprobados</span><strong>{medAprobadas}</strong></div>
                                        <div className='estado-item'><span className='estado-dot' style={{ background: '#F44336' }}></span><span>No Aprobados</span><strong>{medNoAprobadas}</strong></div>
                                        <div className='estado-item'><span className='estado-dot' style={{ background: '#2196F3' }}></span><span>Total</span><strong>{totalMed}</strong></div>
                                      </div>
                                    </div>
                                    <div className='side-panel-card'>
                                      <div className='side-panel-title'>📋 Tipo de Resultado</div>
                                      <div className='limites-grid'>
                                        <div className='limite-row'><span>Tipo:</span><strong style={{ color: '#9C27B0' }}>{chartTipoRes}</strong></div>
                                        <div className='limite-row'><span>N:</span><strong>{totalMed}</strong></div>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          );
                        })
                      )
                    ) : (
                      <div className='no-chart-data'>
                        <p>No hay datos de gráficos disponibles</p>
                      </div>
                    )}
                  </div>
                  </ScrollView>
                    </TabItem>

                    {/* Sub-tab: Selección de Reglas (Method Decision Chart) */}
                    <TabItem title='Selección de Reglas'>
                      <ScrollView width='100%' height='100%'>
                        <div className='sigma-selection-section' style={{ padding: '5px 0' }}>
                          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
                            <span className='analito-badge'>{selectedAnalitoNombre}</span>
                            <span style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                              Gráfico de Selección de Reglas — ubica el método según su CV e inexactitud (Bias) relativos al TEa. Las zonas representan el desempeño Sigma.
                            </span>
                          </div>

                          {/* Leyenda de zonas */}
                          <div className='sigma-zones-legend'>
                            {[
                              { key: 'CLASE_MUNDIAL', label: 'Clase Mundial', range: '≥ 6σ' },
                              { key: 'EXCELENTE', label: 'Excelente', range: '5–6σ' },
                              { key: 'BUENO', label: 'Bueno', range: '4–5σ' },
                              { key: 'MARGINAL', label: 'Marginal', range: '3–4σ' },
                              { key: 'INACEPTABLE', label: 'Inaceptable', range: '< 3σ' },
                            ].map((z) => (
                              <div key={z.key} className='zone-legend-item'>
                                <span className='zone-legend-swatch' style={{ backgroundColor: getSigmaColor(z.key as ClasificacionSigma), opacity: 0.35 }} />
                                <span className='zone-legend-label'>{z.label}</span>
                                <span className='zone-legend-range'>{z.range}</span>
                              </div>
                            ))}
                          </div>

                          {chartsData && chartsData.length > 0 ? (
                            chartsData.map((chartInfo) => {
                              const teaSel = chartInfo.data?.TEa;
                              const sigmaSel = chartInfo.data?.SigmaActual;
                              const cvSel = chartInfo.data?.CVActual;
                              const biasSel = chartInfo.data?.BiasActual;
                              const clasifSel = chartInfo.data?.ClasificacionSigma;
                              const reglasSel: string[] = (chartInfo.data?.ReglasRecomendadas as string[]) || [];
                              const sdSel = chartInfo.data?.DesviacionEstandar;
                              const tipoSel = chartInfo.data?.TipoResultado || 'CUANTITATIVO';

                              if (tipoSel !== 'CUANTITATIVO' || teaSel == null || sigmaSel == null || cvSel == null || biasSel == null) {
                                return (
                                  <div key={chartInfo.controlKey} className='sigma-selection-row' style={{ marginBottom: '15px' }}>
                                    <h5 style={{ margin: '0 0 5px 0', color: '#333', fontSize: '14px' }}>{chartInfo.controlName}</h5>
                                    <div className='no-sigma-data'>
                                      <p>⚠ Esta gráfica requiere un control <strong>CUANTITATIVO</strong> con <strong>TEa</strong> configurado y mediciones suficientes.</p>
                                      {tipoSel === 'CUANTITATIVO' && teaSel == null && (
                                        <p style={{ fontSize: '12px' }}>Configure el TEa desde el módulo de Configuración QC para habilitar esta gráfica.</p>
                                      )}
                                    </div>
                                  </div>
                                );
                              }

                              const absBias = Math.abs(biasSel);
                              const xMax = Math.max(teaSel / 2.5, cvSel * 1.6, 1);
                              const yMax = Math.max(teaSel, absBias * 1.25);
                              const nPoints = 80;
                              const selectionData: any[] = [];
                              for (let i = 0; i <= nPoints; i++) {
                                const cvVal = (xMax * i) / nPoints;
                                const l3 = Math.max(0, teaSel - 3 * cvVal);
                                const l4 = Math.max(0, teaSel - 4 * cvVal);
                                const l5 = Math.max(0, teaSel - 5 * cvVal);
                                const l6 = Math.max(0, teaSel - 6 * cvVal);
                                selectionData.push({
                                  CV: cvVal,
                                  Line3: l3, Line4: l4, Line5: l5, Line6: l6,
                                  GreenBot: 0, GreenTop: l6,
                                  BlueBot: l6, BlueTop: l5,
                                  YellowBot: l5, YellowTop: l4,
                                  OrangeBot: l4, OrangeTop: l3,
                                  RedBot: l3, RedTop: yMax,
                                });
                              }
                              // Inserta el punto del método (ordenado por CV)
                              const l3p = Math.max(0, teaSel - 3 * cvSel);
                              const l4p = Math.max(0, teaSel - 4 * cvSel);
                              const l5p = Math.max(0, teaSel - 5 * cvSel);
                              const l6p = Math.max(0, teaSel - 6 * cvSel);
                              const pointEntry = {
                                CV: cvSel,
                                Line3: l3p, Line4: l4p, Line5: l5p, Line6: l6p,
                                GreenBot: 0, GreenTop: l6p,
                                BlueBot: l6p, BlueTop: l5p,
                                YellowBot: l5p, YellowTop: l4p,
                                OrangeBot: l4p, OrangeTop: l3p,
                                RedBot: l3p, RedTop: yMax,
                                PointBias: absBias,
                              };
                              const insertIdx = selectionData.findIndex((d) => d.CV >= cvSel);
                              if (insertIdx === -1) selectionData.push(pointEntry);
                              else selectionData.splice(insertIdx, 0, pointEntry);

                              const pointColor = getSigmaColor(clasifSel);

                              return (
                                <div key={chartInfo.controlKey} className='sigma-selection-row chart-with-panel' style={{ marginBottom: '15px' }}>
                                  <h5 style={{ margin: '0 0 5px 0', color: '#333', fontSize: '14px' }}>{chartInfo.controlName}</h5>
                                  <div className='chart-layout-row'>
                                    <div className='chart-area'>
                                      <Chart
                                        dataSource={selectionData}
                                        height={420}
                                        customizePoint={(pointInfo: any) => {
                                          if (pointInfo.seriesName === 'Método actual') {
                                            return { color: pointColor, hoverStyle: { color: pointColor } };
                                          }
                                          return {};
                                        }}>
                                        <ArgumentAxis title='CV (%) — Imprecisión' />
                                        <ValueAxis title='|Bias| (%) — Inexactitud' visualRange={[0, yMax]} />
                                        <Animation enabled={true} duration={600} easing='easeOutCubic' />
                                        <CommonSeriesSettings argumentField='CV' />

                                        {/* Zonas */}
                                        <Series type='rangearea' name='Clase Mundial' rangeValue1Field='GreenBot' rangeValue2Field='GreenTop' color={SIGMA_COLORS.CLASE_MUNDIAL} opacity={0.22} showInLegend={false} />
                                        <Series type='rangearea' name='Excelente' rangeValue1Field='BlueBot' rangeValue2Field='BlueTop' color={SIGMA_COLORS.EXCELENTE} opacity={0.22} showInLegend={false} />
                                        <Series type='rangearea' name='Bueno' rangeValue1Field='YellowBot' rangeValue2Field='YellowTop' color={SIGMA_COLORS.BUENO} opacity={0.28} showInLegend={false} />
                                        <Series type='rangearea' name='Marginal' rangeValue1Field='OrangeBot' rangeValue2Field='OrangeTop' color={SIGMA_COLORS.MARGINAL} opacity={0.25} showInLegend={false} />
                                        <Series type='rangearea' name='Inaceptable' rangeValue1Field='RedBot' rangeValue2Field='RedTop' color={SIGMA_COLORS.INACEPTABLE} opacity={0.2} showInLegend={false} />

                                        {/* Líneas de iso-sigma */}
                                        <Series type='line' name='6σ' valueField='Line6' color='#2E7D32' width={2.3} dashStyle='solid'>
                                          <Point visible={false} />
                                        </Series>
                                        <Series type='line' name='5σ' valueField='Line5' color='#1565C0' width={2} dashStyle='solid'>
                                          <Point visible={false} />
                                        </Series>
                                        <Series type='line' name='4σ' valueField='Line4' color='#F9A825' width={2} dashStyle='solid'>
                                          <Point visible={false} />
                                        </Series>
                                        <Series type='line' name='3σ' valueField='Line3' color='#C62828' width={2.3} dashStyle='solid'>
                                          <Point visible={false} />
                                        </Series>

                                        {/* Punto del método */}
                                        <Series type='scatter' name='Método actual' valueField='PointBias' color={pointColor}>
                                          <Point size={18} symbol='circle' visible={true} />
                                        </Series>

                                        <Legend verticalAlignment='bottom' horizontalAlignment='center' />
                                        <ChartTooltip
                                          enabled={true}
                                          shared={false}
                                          zIndex={10000}
                                          customizeTooltip={(pointInfo: any) => {
                                            if (pointInfo.seriesName === 'Método actual') {
                                              return {
                                                html: `
                                                  <div style="padding: 8px; min-width: 160px;">
                                                    <div style="font-weight:700; margin-bottom:4px; color:${pointColor};">Método actual</div>
                                                    <div><strong>CV:</strong> ${cvSel.toFixed(2)}%</div>
                                                    <div><strong>|Bias|:</strong> ${absBias.toFixed(2)}%</div>
                                                    <div><strong>σ:</strong> ${sigmaSel.toFixed(2)}</div>
                                                    <div><strong>Clasificación:</strong> ${getSigmaLabel(clasifSel)}</div>
                                                  </div>
                                                `,
                                              };
                                            }
                                            if (pointInfo.seriesName && pointInfo.seriesName.endsWith('σ')) {
                                              return {
                                                html: `<div style="padding:6px;"><strong>Línea ${pointInfo.seriesName}</strong><br/>|Bias| + ${pointInfo.seriesName.replace('σ', '')}·CV = TEa</div>`,
                                              };
                                            }
                                            return { text: `${pointInfo.seriesName}: ${pointInfo.valueText}` };
                                          }}
                                        />
                                      </Chart>
                                    </div>

                                    {/* Panel lateral */}
                                    <div className='chart-side-panel'>
                                      <div className='side-panel-card sigma-card'>
                                        <div className='side-panel-title'>σ Desempeño Sigma</div>
                                        <div className='sigma-badge-wrapper'>
                                          <span className='sigma-classification-badge' style={{ backgroundColor: pointColor }}>
                                            {getSigmaLabel(clasifSel)}
                                          </span>
                                        </div>
                                        <div className='sigma-big-value' style={{ color: pointColor }}>
                                          {sigmaSel.toFixed(2)}
                                        </div>
                                        <div className='sigma-big-label'>Nivel Sigma</div>
                                        <div className='limites-grid' style={{ marginTop: '8px' }}>
                                          <div className='limite-row'><span>TEa:</span><strong>{teaSel.toFixed(2)}%</strong></div>
                                          <div className='limite-row'><span>SD:</span><strong>{typeof sdSel === 'number' ? sdSel.toFixed(2) : '-'}</strong></div>
                                          <div className='limite-row'><span>CV actual:</span><strong>{cvSel.toFixed(2)}%</strong></div>
                                          <div className='limite-row'><span>|Bias| actual:</span><strong>{absBias.toFixed(2)}%</strong></div>
                                        </div>
                                        {reglasSel && reglasSel.length > 0 && (
                                          <div className='sigma-rules-section'>
                                            <div className='sigma-rules-caption'>Reglas recomendadas:</div>
                                            <div className='sigma-rules-chips'>
                                              {reglasSel.map((r, idx) => (
                                                <span key={idx} className='sigma-rule-chip'>{r}</span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      <div className='side-panel-card' style={{ background: '#f8f9fa', borderLeftColor: '#607D8B' }}>
                                        <div className='side-panel-title'>ⓘ Cómo leer el gráfico</div>
                                        <div style={{ fontSize: '12px', color: '#555', lineHeight: 1.5 }}>
                                          Cada zona coloreada corresponde a un nivel Sigma. El punto se ubica en <strong>(CV, |Bias|)</strong> del método. Cuanto más cercano al origen, mejor el desempeño. Las líneas satisfacen <code>|Bias| + k·CV = TEa</code> con k = 3, 4, 5, 6.
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className='no-chart-data'>
                              <p>No hay datos de métricas sigma disponibles</p>
                            </div>
                          )}
                        </div>
                      </ScrollView>
                    </TabItem>

                    {/* Sub-tab: Datos Históricos */}
                    <TabItem title='Datos Históricos'>
                  <ScrollView width='100%' height='100%'>
                      <div style={{ margin: '8px 0' }}><span className='analito-badge'>{selectedAnalitoNombre}</span></div>
                      <div className='mediciones-grid-section'>
                    <DataGrid className='grid' dataSource={medicionesAnalito} ref={medicionesGridRef} width={'100%'} height={400} hoverStateEnabled={true} repaintChangesOnly={true} highlightChanges={true} rowAlternationEnabled={true} showColumnLines={true}>
                      <HeaderFilter visible={true} />
                      <FilterRow visible={true} />
                      <Scrolling mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
                      <Sorting mode='multiple' />
                      <StateStoring enabled={false} />

                      <Column caption='NIVEL' dataField='NivelNombre' width={120} alignment='center' />
                      <Column
                        caption='FECHA/HORA'
                        dataField='Qcme_FechaHora'
                        width={180}
                        alignment='center'
                        dataType='datetime'
                        format='dd/MM/yyyy HH:mm'
                        defaultSortOrder='desc'
                        sortOrder='desc'
                        sortIndex={0}
                        calculateFilterExpression={(filterValue, selectedFilterOperation, target) => {
                          if (target === 'filterRow' || target === 'headerFilter') {
                            if (filterValue) {
                              const date = new Date(filterValue);
                              date.setHours(0, 0, 0, 0);
                              const nextDate = new Date(date);
                              nextDate.setDate(nextDate.getDate() + 1);
                              if (selectedFilterOperation === '=' || !selectedFilterOperation) {
                                return [['Qcme_FechaHora', '>=', date], 'and', ['Qcme_FechaHora', '<', nextDate]];
                              }
                              if (selectedFilterOperation === '>') return ['Qcme_FechaHora', '>=', nextDate];
                              if (selectedFilterOperation === '>=') return ['Qcme_FechaHora', '>=', date];
                              if (selectedFilterOperation === '<') return ['Qcme_FechaHora', '<', date];
                              if (selectedFilterOperation === '<=') return ['Qcme_FechaHora', '<', nextDate];
                            }
                          }
                          return [['Qcme_FechaHora', selectedFilterOperation || '=', filterValue]];
                        }}
                      />
                      <Column caption='VALOR' dataField='Qcme_Valor' width={100} alignment='center' dataType='number' format='#,##0.00'
                        cellRender={(cellData: any) => {
                          const val = cellData.data.Qcme_Valor;
                          if (val === null || val === undefined) return <div style={{ textAlign: 'center' }}>-</div>;
                          return <div style={{ textAlign: 'center' }}>{typeof val === 'number' ? val.toFixed(2) : val}</div>;
                        }}
                      />
                      <Column caption='RESULTADO' dataField='ResultadoTexto' width={130} alignment='center'
                        cellRender={(cellData: any) => {
                          const texto = cellData.data.ResultadoTexto || cellData.data.Qcme_ValorCualitativo;
                          if (!texto) return <div style={{ textAlign: 'center' }}>-</div>;
                          return <div style={{ textAlign: 'center' }}>{texto}</div>;
                        }}
                      />
                      <Column caption='Z-SCORE' dataField='ZScore' width={100} alignment='center' dataType='number' format='#,##0.00'
                        cellRender={(cellData: any) => {
                          const zs = cellData.data.ZScore;
                          if (zs === null || zs === undefined) return <div style={{ textAlign: 'center' }}>-</div>;
                          return <div style={{ textAlign: 'center' }}>{typeof zs === 'number' ? zs.toFixed(2) : zs}</div>;
                        }}
                      />
                      <Column caption='APROBADO' dataField='AprobadoTexto' width={120} alignment='center'
                        cellRender={(cellData: any) => {
                          const aprobadoTexto = cellData.data.AprobadoTexto;
                          if (!aprobadoTexto) return <div style={{ textAlign: 'center' }}>-</div>;
                          const color = aprobadoTexto === 'APROBADO' ? '#4CAF50' : '#F44336';
                          return (
                            <div style={{ backgroundColor: color, color: 'white', padding: '4px 8px', borderRadius: '4px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px' }}>
                              {aprobadoTexto}
                            </div>
                          );
                        }}
                      />
                      <Column
                        caption='ESTADO'
                        dataField='Qcme_Estado'
                        width={120}
                        alignment='center'
                        cellRender={renderEstadoCeldaMedicion}
                        calculateFilterExpression={(filterValue, selectedFilterOperation) => {
                          if (filterValue === 'PENDIENTE') return ['Qcme_Estado', '=', 1];
                          if (filterValue === 'VALIDADO') return ['Qcme_Estado', '=', 2];
                          if (filterValue === 'RECHAZADO') return ['Qcme_Estado', '=', 3];
                          if (filterValue === 'ALERTA') return ['Qcme_Estado', '=', 4];
                          return ['Qcme_Estado', selectedFilterOperation || '=', filterValue];
                        }}>
                        <HeaderFilter
                          dataSource={[
                            { text: 'PENDIENTE', value: 'PENDIENTE' },
                            { text: 'VALIDADO', value: 'VALIDADO' },
                            { text: 'RECHAZADO', value: 'RECHAZADO' },
                            { text: 'ALERTA', value: 'ALERTA' },
                          ]}
                        />
                      </Column>
                      <Column
                        caption='VIOLACIONES'
                        dataField='TieneViolaciones'
                        width={140}
                        alignment='center'
                        cellRender={renderViolacionCelda}
                        calculateFilterExpression={(filterValue, selectedFilterOperation) => {
                          if (filterValue === 'SI') return ['TieneViolaciones', '=', true];
                          if (filterValue === 'NO') return ['TieneViolaciones', '=', false];
                          return ['TieneViolaciones', selectedFilterOperation || '=', filterValue];
                        }}>
                        <HeaderFilter
                          dataSource={[
                            { text: 'SI', value: 'SI' },
                            { text: 'NO', value: 'NO' },
                          ]}
                        />
                      </Column>
                      <Column caption='TURNO' dataField='Qcme_Turno' width={110} alignment='center' />
                      <Column caption='USUARIO' dataField='UsuarioNombre' width={150} alignment='center' />
                      <Column caption='COMENTARIO / VIOLACIONES' dataField='Qcme_Observaciones' minWidth={250} alignment='left' cellRender={renderComentarioCelda} />
                    </DataGrid>
                      </div>

                      {/* Resumen estadístico por nivel */}
                      {chartsData && chartsData.length > 0 && (
                        <div className='stats-summary-section'>
                          <h5 style={{ margin: '0 0 10px 0', color: '#333' }}>Resumen Estadístico</h5>
                          <div className='stats-cards-row'>
                            {chartsData.map((chartInfo) => {
                              const tipoResCard = chartInfo.data?.TipoResultado || 'CUANTITATIVO';
                              const esCuantCard = tipoResCard !== 'CUALITATIVO';
                              const media = chartInfo.data?.Media || 0;
                              const sd = chartInfo.data?.DesviacionEstandar || 0;
                              const cv = media > 0 ? ((sd / media) * 100).toFixed(2) : '0.00';
                              const total = chartInfo.data?.TotalMediciones || 0;
                              const validadas = chartInfo.data?.MedicionesValidadas || 0;
                              const violaciones = chartInfo.data?.MedicionesConViolaciones || 0;
                              const conformidad = total > 0 ? ((validadas / total) * 100).toFixed(1) : '0.0';
                              const aprobadas = chartInfo.data?.MedicionesAprobadas || 0;
                              const noAprobadas = chartInfo.data?.MedicionesNoAprobadas || 0;
                              const pctAprob = total > 0 ? ((aprobadas / total) * 100).toFixed(1) : '0.0';

                              const sigmaActual = chartInfo.data?.SigmaActual;
                              const teaCard = chartInfo.data?.TEa;
                              const clasifCard = chartInfo.data?.ClasificacionSigma;

                              return (
                                <div key={chartInfo.controlKey} className='stats-card'>
                                  <div className='stats-card-header'>{chartInfo.controlName}</div>
                                  <div className='stats-card-body'>
                                    <div className='stat-row'><span>N:</span><strong>{total}</strong></div>
                                    {esCuantCard ? (
                                      <>
                                        <div className='stat-row'><span>Media:</span><strong>{media.toFixed(2)}</strong></div>
                                        <div className='stat-row'><span>SD:</span><strong>{sd.toFixed(2)}</strong></div>
                                        <div className='stat-row'><span>CV:</span><strong>{cv}%</strong></div>
                                        <div className='stat-row'><span>Aceptabilidad:</span><strong style={{ color: parseFloat(conformidad) >= 80 ? '#4CAF50' : '#FF9800' }}>{conformidad}%</strong></div>
                                        <div className='stat-row'><span>Validadas:</span><strong style={{ color: '#4CAF50' }}>{validadas}</strong></div>
                                        <div className='stat-row'><span>Violaciones:</span><strong style={{ color: violaciones > 0 ? '#F44336' : '#4CAF50' }}>{violaciones}</strong></div>
                                        {teaCard != null && sigmaActual != null && (
                                          <>
                                            <div className='stat-row'><span>TEa:</span><strong>{typeof teaCard === 'number' ? teaCard.toFixed(2) + '%' : '-'}</strong></div>
                                            <div className='stat-row'>
                                              <span>Nivel σ:</span>
                                              <strong style={{ color: getSigmaColor(clasifCard) }}>
                                                {typeof sigmaActual === 'number' ? sigmaActual.toFixed(2) : '-'}
                                              </strong>
                                            </div>
                                            <div className='stat-row'>
                                              <span>Clasificación:</span>
                                              <strong>
                                                <span className='sigma-classification-chip' style={{ backgroundColor: getSigmaColor(clasifCard) }}>
                                                  {getSigmaLabel(clasifCard)}
                                                </span>
                                              </strong>
                                            </div>
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <div className='stat-row'><span>Tipo:</span><strong style={{ color: '#9C27B0' }}>{tipoResCard}</strong></div>
                                        <div className='stat-row'><span>Aprobación:</span><strong style={{ color: parseFloat(pctAprob) >= 80 ? '#4CAF50' : '#FF9800' }}>{pctAprob}%</strong></div>
                                        <div className='stat-row'><span>Aprobados:</span><strong style={{ color: '#4CAF50' }}>{aprobadas}</strong></div>
                                        <div className='stat-row'><span>No Aprobados:</span><strong style={{ color: noAprobadas > 0 ? '#F44336' : '#4CAF50' }}>{noAprobadas}</strong></div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                  </ScrollView>
                    </TabItem>
                  </TabPanel>
                </div>
              ) : (
                <div className='no-analito-selected'>
                  <p>Seleccione un analito del grid de ingreso para ver su detalle</p>
                </div>
              )}
            </TabItem>

            {/* Pestaña 3: Informes Estadísticos Six Sigma */}
            {Prm_QC_InformesSigma && (
              <TabItem title='INFORMES'>
                <QcReports configuraciones={configuracionesCache} defaultSedeId={userLocation} />
              </TabItem>
            )}
          </TabPanel>
        </ScrollView>

        {/* Modal para agregar/editar nota */}
        <Popup
          visible={notaModalVisible}
          onHiding={() => {
            setNotaModalVisible(false);
            setNotaActual('');
            setNotaAnalitoId(null);
            setNotaMaterialLoteKey('');
          }}
          dragEnabled={false}
          closeOnOutsideClick={true}
          showTitle={true}
          title='Nota de Medición'
          width={500}
          height={300}>
          <div style={{ padding: '10px' }}>
            <TextArea value={notaActual} onValueChanged={(e) => setNotaActual(e.value)} height={150} placeholder='Ingrese una nota para esta medición...' />
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button
                text='Cancelar'
                onClick={() => {
                  setNotaModalVisible(false);
                  setNotaActual('');
                  setNotaAnalitoId(null);
                  setNotaMaterialLoteKey('');
                }}
                stylingMode='outlined'
              />
              <Button text='Guardar' onClick={handleSaveNota} type='default' stylingMode='contained' />
            </div>
          </div>
        </Popup>
      </div>
    </div>
  );
};

export default MasiveMeasurementsQC;
